import { useEffect, useRef, useState } from "react";

const isSupported = () => typeof IntersectionObserver !== "undefined";

/**
 * Whether the element behind `ref` is on screen or within `rootMargin` of it.
 *
 * Meant for long lists where every row carries something heavy (a product
 * image, a chart, a data grid): render that part only while this returns true
 * and it costs memory only for the rows the user can actually reach, while the
 * row itself stays mounted so Cmd+F, filters and expand-all still cover the
 * whole list. Leaving the zone flips the value back only after `leaveDelayMs`,
 * so a row scrolled past and straight back does not remount everything.
 *
 * `onChange(isNear)` runs synchronously inside the observer callback, before
 * any re-render, for callers that need to measure or capture the content
 * before it goes away.
 *
 * Without IntersectionObserver (jsdom) the hook stays true, so nothing is ever
 * hidden where it cannot be observed.
 */
export default function useNearViewport(
  ref,
  { rootMargin = "800px 0px", leaveDelayMs = 500, onChange } = {}
) {
  const [near, setNear] = useState(() => !isSupported());
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const element = ref.current;
    if (!element || !isSupported()) return undefined;

    let leaveTimer = null;
    const cancelLeave = () => {
      if (leaveTimer) {
        clearTimeout(leaveTimer);
        leaveTimer = null;
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        // Several notifications can queue up in one callback; only the last
        // one says where the element is now.
        const isNear = entries[entries.length - 1].isIntersecting;
        if (onChangeRef.current) onChangeRef.current(isNear);
        if (isNear) {
          cancelLeave();
          setNear(true);
        } else if (leaveDelayMs > 0) {
          if (!leaveTimer) {
            leaveTimer = setTimeout(() => {
              leaveTimer = null;
              setNear(false);
            }, leaveDelayMs);
          }
        } else {
          setNear(false);
        }
      },
      { rootMargin }
    );
    observer.observe(element);

    return () => {
      cancelLeave();
      observer.disconnect();
    };
  }, [ref, rootMargin, leaveDelayMs]);

  return near;
}
